# Step 4: Download resume

When a candidate applies using Apply with SEEK they may have a resume attached to their profile
Partners can download attachment files associated with a particular candidate profile to pre-fill the resume field.

The [`candidateProfile` query from Step 3] should be used to retrieve the URL of any profile attachments.
If a resume is available, the attachments object should be populated with the URL of the resume as shown below.

[`candidateProfile` query from Step 3]: /use-cases/apply-with-seek/retrieve-seek-profile

```graphql
query ($id: String!) {
  candidateProfile(id: $id) {
    attachments {
      descriptions
      seekRoleCode
      url
    }
  }
}
```

```json
{
  "id": "globalPublicTest:candidateProfile:prefill:PoJo9PPLL9wEF6z5nvMP9d"
}
```

```json
{
  "candidateProfile": {
    "attachments": [
      {
        "descriptions": ["Candidate resume"],
        "seekRoleCode": "Resume",
        "url": "https://graphql.seek.com/globalPublicTest/prefillProfiles/PoJo9PPLL9wEF6z5nvMP9d/attachments/KEGE8Lgav8Kx9jYoxKHuVv"
      }
    ]
  }
}
```

## Downloading attachment files

Because GraphQL does not efficiently support binary data, the attachment itself is provided over HTTPS.
Each attachment has a unique download URL provided in the [`url` field] of the GraphQL response.

During the Apply with SEEK flow your software should download attachments via your server using a [partner token].

[partner token]: /auth/partner-tokens
[`url` field]: /schema/named-type/Attachment#/field/url

## Attachment file metadata

Attachment files have additional metadata returned in standard HTTP headers.
These headers can be parsed by your software or passed to the hirer’s browser when they download the attachment.

You can use the [HTTP `HEAD` method] to retrieve an attachment’s metadata without downloading its contents.
This is useful to display the attachment’s metadata before downloading it.

### `Content-Disposition`

The [`Content-Disposition`] header contains the attachment’s original filename encoded using [RFC 6266].

We recommend parsing the filename using a library or framework that supports [RFC 6266].
This ensures your software is robust against filenames containing spaces, quotes or other special characters.

### `Content-Length`

The [`Content-Length`] header indicates the size of the attachment in bytes.
This can be retrieved using the [HTTP `HEAD` method] to display the attachment’s size before downloading it.

### `Content-Type`

The [`Content-Type`] header indicates the file format of the attachment.
Alternatively, you can extract the attachment’s file extension from its [`Content-Disposition`](#content-disposition).

The currently supported file formats are:

| `Content-Type`                                                            | File format                    | Extension |
| ------------------------------------------------------------------------- | ------------------------------ | --------- |
| `application/msword`                                                      | Microsoft Word 97-2004         | `.doc`    |
| `application/vnd.openxmlformats-officedocument.wordprocessingml.document` | Microsoft Word (OpenXML)       | `.docx`   |
| `application/rtf`                                                         | Rich Text Format (RTF)         | `.rtf`    |
| `application/pdf`                                                         | Portable Document Format (PDF) | `.pdf`    |
| `text/plain`                                                              | Plain text                     | `.txt`    |

### `Last-Modified`

The [`Last-Modified`] header contains the date the attachment was uploaded or generated.

## Security

SEEK processes attachments to reduce the risk to hirers reviewing candidate applications:

- Attachments are scanned for viruses before the candidate application is made visible to partners.
  Any files containing viruses are removed from the candidate application.

- Attachments may be modified to remove dangerous content such as macros or phishing links.

The precise details of SEEK’s attachment processing are proprietary and subject to change.
SEEK continuously improves its processes in response to new threats.

[`content-disposition`]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Disposition
[`content-length`]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Length
[`content-type`]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Type
[http `head` method]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Methods/HEAD
[`last-modified`]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Last-Modified
[rfc 6266]: https://datatracker.ietf.org/doc/html/rfc6266
